package org.zq.astrock_ai.ui.research.gushi.data

import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.zq.astrock_ai.api.dto.RecommendResponseDto
import org.zq.astrock_ai.storage.ResearchKvStorage
import org.zq.astrock_ai.util.currentTimeMillis
import org.zq.astrock_ai.util.formatDecimal

@Serializable
data class RecommendHistoryStock(
    val code: String,
    val name: String = "",
    val price: Double = 0.0,
    val buyLow: Double = 0.0,
    val buyHigh: Double = 0.0,
    val sellLow: Double = 0.0,
    val sellHigh: Double = 0.0,
)

@Serializable
data class RecommendHistorySnapshot(
    val atMs: Long,
    val boardsLabel: String = "",
    val stocks: List<RecommendHistoryStock> = emptyList(),
)

@Serializable
private data class RecommendHistoryFile(
    val version: Int = 1,
    val entries: List<RecommendHistorySnapshot> = emptyList(),
)

object RecommendHistoryStore {
    private const val KEY = "recommend.history.v1"
    private const val MAX_ENTRIES = 500
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
    }

    fun load(): List<RecommendHistorySnapshot> {
        val all = runCatching {
            val raw = ResearchKvStorage.get(KEY) ?: return emptyList()
            json.decodeFromString<RecommendHistoryFile>(raw).entries
        }.getOrDefault(emptyList())
        return all.sortedByDescending { it.atMs }.take(MAX_ENTRIES)
    }

    fun append(result: RecommendResponseDto): RecommendHistorySnapshot {
        val snapshot = RecommendHistorySnapshot(
            atMs = currentTimeMillis(),
            boardsLabel = result.boardsLabel,
            stocks = result.stocks.map { s ->
                RecommendHistoryStock(
                    code = s.code,
                    name = s.name,
                    price = s.price,
                    buyLow = s.buyLow,
                    buyHigh = s.buyHigh,
                    sellLow = s.sellLow,
                    sellHigh = s.sellHigh,
                )
            },
        )
        val next = (listOf(snapshot) + load()).sortedByDescending { it.atMs }.take(MAX_ENTRIES)
        ResearchKvStorage.put(KEY, json.encodeToString(RecommendHistoryFile(entries = next)))
        return snapshot
    }

    fun uniqueDates(
        entries: List<RecommendHistorySnapshot> = load(),
        nowMs: Long = currentTimeMillis(),
    ): List<RecommendHistoryDate> {
        val grouped = entries.groupBy { chinaEpochDay(it.atMs) }
        return grouped.keys.sortedDescending().map { day ->
            val sample = grouped.getValue(day).maxBy { it.atMs }
            RecommendHistoryDate(
                epochDay = day,
                date = formatRecommendHistoryDate(sample.atMs),
                label = formatRecommendHistoryDateLabel(sample.atMs, nowMs),
                count = grouped.getValue(day).sumOf { snap -> snap.stocks.size.coerceAtLeast(1) },
            )
        }
    }

    fun forDate(
        entries: List<RecommendHistorySnapshot> = load(),
        epochDay: Int,
    ): List<RecommendHistorySnapshot> =
        entries.filter { chinaEpochDay(it.atMs) == epochDay }.sortedByDescending { it.atMs }
}

data class RecommendHistoryDate(
    val epochDay: Int,
    val date: String,
    val label: String,
    val count: Int,
)

/** 东八区日历日序号（自 1970-01-01 起）。 */
fun chinaEpochDay(atMs: Long): Int {
    val chinaMs = atMs + 8L * 3_600_000L
    return (chinaMs / 86_400_000L).toInt()
}

fun formatRecommendHistoryDate(atMs: Long): String {
    val (year, month, day) = chinaYearMonthDay(atMs)
    return "$year-${pad2(month)}-${pad2(day)}"
}

fun formatRecommendHistoryDateLabel(atMs: Long, nowMs: Long = currentTimeMillis()): String {
    val date = formatRecommendHistoryDate(atMs)
    val delta = chinaEpochDay(nowMs) - chinaEpochDay(atMs)
    return when (delta) {
        0 -> "$date 今天"
        1 -> "$date 昨天"
        else -> date
    }
}

fun formatRecommendHistoryTime(atMs: Long): String {
    val chinaMs = atMs + 8L * 3_600_000L
    val totalSec = (chinaMs / 1000L).coerceAtLeast(0L)
    val secInDay = (totalSec % 86_400L).toInt()
    val hour = secInDay / 3600
    val minute = (secInDay % 3600) / 60
    val (year, month, day) = chinaYearMonthDay(atMs)
    return "$year-${pad2(month)}-${pad2(day)} ${pad2(hour)}:${pad2(minute)}"
}

fun chinaYearMonthDay(atMs: Long): Triple<Int, Int, Int> {
    val chinaMs = atMs + 8L * 3_600_000L
    val totalSec = (chinaMs / 1000L).coerceAtLeast(0L)
    var days = (totalSec / 86_400L).toInt()
    var year = 1970
    while (true) {
        val diy = if (isLeapYear(year)) 366 else 365
        if (days < diy) break
        days -= diy
        year++
        if (year > 2100) break
    }
    val mdays = intArrayOf(
        31,
        if (isLeapYear(year)) 29 else 28,
        31, 30, 31, 30, 31, 31, 30, 31, 30, 31,
    )
    var month = 1
    for (m in mdays) {
        if (days < m) break
        days -= m
        month++
    }
    return Triple(year, month, days + 1)
}

private fun isLeapYear(year: Int): Boolean =
    (year % 4 == 0 && year % 100 != 0) || year % 400 == 0

private fun pad2(n: Int): String = n.toString().padStart(2, '0')

fun chinaCivilToEpochMs(
    year: Int,
    month: Int,
    day: Int,
    hour: Int = 0,
    minute: Int = 0,
): Long {
    var days = 0
    var y = 1970
    while (y < year && y < 2200) {
        days += if (isLeapYear(y)) 366 else 365
        y++
    }
    val mdays = intArrayOf(
        31,
        if (isLeapYear(year)) 29 else 28,
        31, 30, 31, 30, 31, 31, 30, 31, 30, 31,
    )
    var m = 1
    while (m < month && m <= 12) {
        days += mdays[m - 1]
        m++
    }
    days += (day - 1).coerceAtLeast(0)
    val chinaMs = days * 86_400_000L +
        hour.coerceIn(0, 23) * 3_600_000L +
        minute.coerceIn(0, 59) * 60_000L
    return chinaMs - 8L * 3_600_000L
}

fun formatPriceRange(low: Double, high: Double): String = when {
    low > 0 && high > 0 && low != high ->
        "${formatDecimal(low)}–${formatDecimal(high)}"
    low > 0 -> formatDecimal(low)
    high > 0 -> formatDecimal(high)
    else -> "--"
}

fun formatHistoryPrice(v: Double): String =
    if (v > 0) formatDecimal(v) else "--"
